class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshThenOpen(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.refreshThenOpen(cartLink);
      }
    });
  }

  // Re-fetches the drawer's own markup before showing it. This guarantees
  // the drawer never shows stale totals/quantities/free-shipping progress
  // if the cart changed elsewhere in the same tab without a full page
  // reload (e.g. quantity edited on the full /cart page, then the header
  // cart icon is clicked). Falls back to opening with whatever is
  // currently in the DOM if the fetch fails, so opening the drawer never
  // breaks.
  refreshThenOpen(triggeredBy) {
    const cartDrawerNode = document.getElementById('CartDrawer');
    const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';

    if (!cartDrawerNode) {
      this.open(triggeredBy);
      return;
    }

    fetch(`${cartUrl}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
        const targetInner = cartDrawerNode.querySelector('.drawer__inner');
        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }

        const sourceEmpty = html.querySelector('cart-drawer');
        if (sourceEmpty) this.classList.toggle('is-empty', sourceEmpty.classList.contains('is-empty'));
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.open(triggeredBy);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('.drawer__inner');

      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');

      if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, 50);
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    this.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const container =
        section.id === 'cart-drawer'
          ? document.getElementById('CartDrawer')
          : document.getElementById(section.id);

      if (!container || !parsedState.sections[section.id]) return;

      const newContent = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (newContent !== null) {
        container.innerHTML = newContent;
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    }, 0);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // NOTE: `id` must be the real DOM id document.getElementById() will find.
  // The drawer wrapper itself has no id — only its inner #CartDrawer node
  // does — so this must target #CartDrawer and select .drawer__inner
  // (which contains both the line items AND the footer: totals, free
  // shipping bar, checkout button) so quantity changes refresh everything
  // in one pass. This matches the contract documented in
  // standard-actions-override.js (DAWN_PUBSUB_REFRESHED_SECTIONS:
  // 'cart-drawer-items:CartDrawer').
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#shopify-section-cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);